import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/appStore';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export default function Header() {
  const { t, i18n } = useTranslation();
  const { language, setLanguage } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(language);

  const languages = [
    { code: 'ar', name: 'العربية' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'de', name: 'Deutsch' },
    { code: 'tr', name: 'Türkçe' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'fa', name: 'فارسی' },
    { code: 'ur', name: 'اردو' },
    { code: 'zh', name: '中文' },
  ];

  useEffect(() => {
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  const handleLanguageChange = async (code) => {
    try {
      // Change language in i18n (this reactively updates all t() calls
      // and the lang/dir attributes via the listener in i18n.js)
      await i18n.changeLanguage(code);

      // Update store
      setLanguage(code);
      setCurrentLang(code);

      // Close mobile menu
      setMobileMenuOpen(false);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-maroon to-primary shadow-lg backdrop-blur-md bg-opacity-95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition"
            onClick={() => handleNavigation('/')}
          >
            <div className="w-8 h-8 bg-gradient-to-br from-accent to-light rounded-lg flex items-center justify-center shadow-md">
              <span className="text-maroon font-bold text-sm">SB</span>
            </div>
            <h1 className="text-xl font-bold text-light gradient-text">SocialBoost</h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button
              onClick={() => handleNavigation('/')}
              className={`font-semibold transition ${
                isActive('/') 
                  ? 'text-accent border-b-2 border-accent pb-1' 
                  : 'text-light hover:text-accent'
              }`}
            >
              {t('nav.home') || 'الرئيسية'}
            </button>
            <a 
              href="#services" 
              className="text-light hover:text-accent font-semibold transition"
            >
              {t('nav.services') || 'الخدمات'}
            </a>
            <a 
              href="#pricing" 
              className="text-light hover:text-accent font-semibold transition"
            >
              {t('nav.pricing') || 'الأسعار'}
            </a>
            <a 
              href="#contact" 
              className="text-light hover:text-accent font-semibold transition"
            >
              {t('nav.contact') || 'التواصل'}
            </a>
            <button
              onClick={() => handleNavigation('/dashboard')}
              className={`font-semibold transition ${
                isActive('/dashboard') 
                  ? 'text-accent border-b-2 border-accent pb-1' 
                  : 'text-light hover:text-accent'
              }`}
            >
              {t('dashboard.title') || 'لوحة التحكم'}
            </button>
          </nav>

          {/* Language Selector and Mobile Menu */}
          <div className="flex items-center gap-4">
            {/* Desktop Language Selector */}
            <div className="hidden md:flex items-center gap-1 bg-light/10 backdrop-blur-sm rounded-lg p-1 border border-light/20 overflow-x-auto">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`px-2 py-1 rounded text-xs font-bold transition whitespace-nowrap ${
                    currentLang === lang.code
                      ? 'bg-accent text-light shadow-md'
                      : 'text-light hover:bg-light/20'
                  }`}
                  title={lang.name}
                >
                  {lang.code.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-light hover:text-accent transition"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-light/20 animate-slide-down">
            <nav className="flex flex-col gap-4 py-4">
              <button
                onClick={() => handleNavigation('/')}
                className={`text-left font-semibold transition ${
                  isActive('/') 
                    ? 'text-accent' 
                    : 'text-light hover:text-accent'
                }`}
              >
                {t('nav.home') || 'الرئيسية'}
              </button>
              <a href="#services" className="text-light hover:text-accent font-semibold transition">
                {t('nav.services') || 'الخدمات'}
              </a>
              <a href="#pricing" className="text-light hover:text-accent font-semibold transition">
                {t('nav.pricing') || 'الأسعار'}
              </a>
              <a href="#contact" className="text-light hover:text-accent font-semibold transition">
                {t('nav.contact') || 'التواصل'}
              </a>
              <button
                onClick={() => handleNavigation('/dashboard')}
                className={`text-left font-semibold transition ${
                  isActive('/dashboard') 
                    ? 'text-accent' 
                    : 'text-light hover:text-accent'
                }`}
              >
                {t('dashboard.title') || 'لوحة التحكم'}
              </button>
            </nav>
            
            {/* Mobile Language Selector */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-light/20">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`px-3 py-1 rounded text-xs font-bold transition ${
                    currentLang === lang.code
                      ? 'bg-accent text-light shadow-md'
                      : 'text-light hover:bg-light/20'
                  }`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
